import React, { Component, ReactNode } from 'react'
import { Container, Typography, Button, Box, Alert } from '@mui/material'
import i18n from '../i18n'
import { translateText } from '../locales/translateText'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
  errorInfo?: React.ErrorInfo
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    this.setState({ errorInfo })
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined })
  }

  t = (key: string, fallback: string, options?: Record<string, unknown>) =>
    translateText(i18n.t, key, fallback, options)

  render() {
    if (this.state.hasError) {
      return (
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {this.t('errorBoundary.title', '페이지 로딩 중 오류가 발생했습니다')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {this.state.error?.message || this.t('errorBoundary.unknownError', '알 수 없는 오류가 발생했습니다')}
            </Typography>
          </Alert>

          <Box sx={{ mb: 3 }}>
            <Button 
              variant="contained" 
              onClick={this.handleReset}
              sx={{ mr: 2 }}
            >
              {this.t('errorBoundary.retry', '다시 시도')}
            </Button>
            <Button 
              variant="outlined" 
              onClick={() => window.location.href = '/'}
            >
              {this.t('errorBoundary.home', '홈으로 이동')}
            </Button>
          </Box>

          {process.env.NODE_ENV === 'development' && this.state.error && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                {this.t('errorBoundary.developerInfo', '개발자 정보:')}
              </Typography>
              <Box 
                component="pre" 
                sx={{ 
                  bgcolor: 'grey.100', 
                  p: 2, 
                  borderRadius: 1, 
                  fontSize: '0.875rem',
                  overflow: 'auto',
                  maxHeight: '300px'
                }}
              >
                {this.state.error.stack}
              </Box>
            </Box>
          )}
        </Container>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
